import React, { useState } from "react";
import { RxCross2 } from "react-icons/rx";
import Select from "react-select";
import {
  HEARD_ABOUT_US_CONTANTS,
  HEARD_ABOUT_US_MAPPER,
} from "../../constants/common";

const normalizeSalary = (value) => {
  if (!value) return "";
  if (typeof value === "number") return value.toString();
  if (typeof value === "string") {
    const numeric = value.replace(/[^\d]/g, "");
    return numeric || "";
  }
  return "";
};

const phoneNumberFormatter = (value) => {
  if (!value) return "";

  let digits = value.replace(/\D/g, "");

  if (digits.length > 10) {
    digits = digits.replace(/^\d{1,3}/, "");
  }

  return digits;
};

const EditCandidateModal = ({
  openModal,
  handleSave,
  initialData = {},
}) => {
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({ ...initialData, phone: phoneNumberFormatter(initialData.phone || ""), currentsalary: normalizeSalary(initialData.currentsalary || ""), expectedsalary: normalizeSalary(initialData.expectedsalary || "") });
  const [showClosePopup, setShowClosePopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const initEmail = initialData.email;

  const validateForm = (data = formData) => {
    const newErrors = {};

    // Basic info
    if (!data.name?.trim()) newErrors.name = "Name is required";

    if (!data.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!data.phone?.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(data.phone)) {
      newErrors.phone = "Enter a valid 10-digit phone number";
    }

    if (!data.heardaboutus?.trim())
      newErrors.heardaboutus = "Source is required";

    // Conditional fields
    if (data.heardaboutus === HEARD_ABOUT_US_MAPPER["Employees referral"]) {
      if (!data.refferre_name?.trim())
        newErrors.refferre_name = "Referee name is required";
      if (!data.employee_id?.trim())
        newErrors.employee_id = "Employee ID is required";
    }

    if (data.heardaboutus === HEARD_ABOUT_US_MAPPER["Recruitment agency"]) {
      if (!data.agency_name?.trim())
        newErrors.agency_name = "Agency name is required";
    }

    // Salary fields
    const curr = Number(data.currentsalary);
    const exp = Number(data.expectedsalary);

    if (curr === 0 ? false : curr < 0 || !curr) newErrors.currentsalary = "Current salary is required";

    if (exp === 0 ? false : exp < 0 || !exp) newErrors.expectedsalary = "Expected salary is required";

    if (data.joiningtime && data.joiningtime < 0) newErrors.joiningtime = "Please enter a valid joining time";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);
    validateForm(updated);
  };

  const handleSelectChange = (option) => {
    const updated = {
      ...formData,
      heardaboutus: option?.value || "",
      refferre_name: "",
      employee_id: "",
      agency_name: "",
    };
    setFormData(updated);
    validateForm(updated);
  };

  const onSubmit = () => {
    setLoading(true);
    if (validateForm()) {
      const formattedData = {
        ...formData,
        currentsalary: formData.currentsalary
          ? `INR ${Number(formData.currentsalary).toLocaleString("en-IN")}`
          : "",
        expectedsalary: formData.expectedsalary
          ? `INR ${Number(formData.expectedsalary).toLocaleString("en-IN")}`
          : "",
      };

      if (initEmail === formData.email) delete formattedData.email;

      handleSave(formattedData);
    }
    setLoading(false);
  };

  const showReferralFields =
    formData.heardaboutus === HEARD_ABOUT_US_MAPPER["Employees referral"];
  const showAgencyField =
    formData.heardaboutus === HEARD_ABOUT_US_MAPPER["Recruitment agency"];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      {
        showClosePopup ?
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
            <div className="bg-white rounded-xl shadow-2xl border border-gray-300 w-[400px] p-6 animate-fadeIn text-center">
              <h2 className="text-lg font-semibold text-gray-800">
                Are you sure you want to close without saving?
              </h2>
              <p className="text-gray-600 mt-2">
                All the changes you have made will be discarded.
              </p>

              <div className="flex justify-center gap-4 mt-6">
                <button
                  onClick={() => openModal(false)} // ✅ Closes main modal
                  className="bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded-md transition font-medium"
                >
                  Yes, Close
                </button>
                <button
                  onClick={() => setShowClosePopup(false)} // ✅ Hides this confirmation only
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-5 py-2 rounded-md transition font-medium"
                >
                  No, Go Back
                </button>
              </div>
            </div>
          </div>
          :
          <div className="bg-white rounded-xl shadow-2xl border border-gray-300 w-[480px] animate-fadeIn">
            {/* Header */}
            <div className="flex justify-between items-start px-6 py-4 border-b">
              <div>
                <h2 className="text-xl font-semibold">Edit Candidate Details</h2>
                <p className="text-gray-500 text-sm">
                  Update the candidate’s information below
                </p>
              </div>
              <RxCross2
                className="text-2xl cursor-pointer text-gray-500 hover:text-gray-800"
                onClick={() => setShowClosePopup(true)}
              />
            </div>

            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
              <form className="flex flex-col gap-4 text-sm">
                <div>
                  <label className="font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name || ""}
                    onChange={handleChange}
                    placeholder="Enter name"
                    className={`w-full border px-3 py-2 rounded-md mt-1 focus:outline-none focus:ring-2 ${errors.name
                      ? "border-red-400 focus:ring-red-300"
                      : "border-gray-300 focus:ring-blue-400"
                      }`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email || ""}
                    onChange={handleChange}
                    placeholder="Enter email"
                    className={`w-full border px-3 py-2 rounded-md mt-1 focus:outline-none focus:ring-2 ${errors.email
                      ? "border-red-400 focus:ring-red-300"
                      : "border-gray-300 focus:ring-blue-400"
                      }`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="font-medium text-gray-700">Phone</label>
                    <input
                      type="number"
                      name="phone"
                      value={formData.phone || ""}
                      onChange={handleChange}
                      placeholder="Enter phone"
                      className={`w-full border px-3 py-2 rounded-md mt-1 focus:outline-none focus:ring-2 ${errors.phone
                        ? "border-red-400 focus:ring-red-300"
                        : "border-gray-300 focus:ring-blue-400"
                        }`}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                    )}
                  </div>

                  <div className="flex-1">
                    <label className="font-medium text-gray-700">Source</label>
                    <Select
                      className="w-full mt-1"
                      value={
                        formData.heardaboutus
                          ? {
                            value: formData.heardaboutus,
                            label: formData.heardaboutus,
                          }
                          : null
                      }
                      onChange={handleSelectChange}
                      options={HEARD_ABOUT_US_CONTANTS}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                    {errors.heardaboutus && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.heardaboutus}
                      </p>
                    )}
                  </div>
                </div>

                {showReferralFields && (
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="font-medium text-gray-700">
                        Referee Name
                      </label>
                      <input
                        type="text"
                        name="refferre_name"
                        value={formData.refferre_name || ""}
                        onChange={handleChange}
                        placeholder="Enter referee name"
                        className={`w-full border px-3 py-2 rounded-md mt-1 ${errors.refferre_name
                          ? "border-red-400 focus:ring-red-300"
                          : "border-gray-300 focus:ring-blue-400"
                          }`}
                      />
                      {errors.refferre_name && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.refferre_name}
                        </p>
                      )}
                    </div>
                    <div className="flex-1">
                      <label className="font-medium text-gray-700">
                        Employee ID
                      </label>
                      <input
                        type="text"
                        name="employee_id"
                        value={formData.employee_id || ""}
                        onChange={handleChange}
                        placeholder="Enter employee ID"
                        className={`w-full border px-3 py-2 rounded-md mt-1 ${errors.employee_id
                          ? "border-red-400 focus:ring-red-300"
                          : "border-gray-300 focus:ring-blue-400"
                          }`}
                      />
                      {errors.employee_id && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.employee_id}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {showAgencyField && (
                  <div>
                    <label className="font-medium text-gray-700">Agency Name</label>
                    <input
                      type="text"
                      name="agency_name"
                      value={formData.agency_name || ""}
                      onChange={handleChange}
                      placeholder="Enter agency name"
                      className={`w-full border px-3 py-2 rounded-md mt-1 ${errors.agency_name
                        ? "border-red-400 focus:ring-red-300"
                        : "border-gray-300 focus:ring-blue-400"
                        }`}
                    />
                    {errors.agency_name && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.agency_name}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="font-medium text-gray-700">
                      Current Annual Salary
                    </label>
                    <input
                      type="number"
                      name="currentsalary"
                      value={formData.currentsalary || ""}
                      onChange={handleChange}
                      onWheel={(e) => e.target.blur()}
                      placeholder="Enter current salary"
                      className={`w-full border px-3 py-2 rounded-md mt-1 ${errors.currentsalary
                        ? "border-red-400 focus:ring-red-300"
                        : "border-gray-300 focus:ring-blue-400"
                        }`}
                    />
                    {errors.currentsalary && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.currentsalary}
                      </p>
                    )}
                  </div>

                  <div className="flex-1">
                    <label className="font-medium text-gray-700">
                      Expected Annual Salary
                    </label>
                    <input
                      type="number"
                      name="expectedsalary"
                      value={formData.expectedsalary || ""}
                      onChange={handleChange}
                      onWheel={(e) => e.target.blur()}
                      placeholder="Enter expected salary"
                      className={`w-full border px-3 py-2 rounded-md mt-1 ${errors.expectedsalary
                        ? "border-red-400 focus:ring-red-300"
                        : "border-gray-300 focus:ring-blue-400"
                        }`}
                    />
                    {errors.expectedsalary && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.expectedsalary}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="font-medium text-gray-700">Time to Join(in days)</label>
                  <input
                    type="number"
                    name="joiningtime"
                    onWheel={(e) => e.target.blur()}
                    value={formData.joiningtime || ""}
                    onChange={handleChange}
                    placeholder="Enter time to join"
                    className={`w-full border px-3 py-2 rounded-md mt-1 ${errors.joiningtime
                      ? "border-red-400 focus:ring-red-300"
                      : "border-gray-300 focus:ring-blue-400"
                      }`}
                  />
                  {errors.joiningtime && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.joiningtime}
                    </p>
                  )}
                </div>
              </form>
            </div>

            <div className="flex justify-end bg-gray-50 px-6 py-4 rounded-b-xl">
              <button
                className="bg-[#148EFF] text-white font-medium py-2 px-5 rounded-md hover:bg-blue-600 transition disabled:opacity-50"
                onClick={onSubmit}
                type="submit"
                disabled={Object.values(errors).some((error) => error.length > 0) || loading}
              >
                Save
              </button>
            </div>
          </div>
      }
    </div>
  );
};

export default EditCandidateModal;
